// Client/src/components/Sidebar/RSAAnalysis/PathsPanels/LrsRoutePanel.jsx
// "Add route by road / milepost" — creates a route from the state LRS instead
// of two map clicks. Resolves road + measures to two lat/lng waypoints and
// hands them to the existing path pipeline (addPath + updatePath; the map's
// PathMapContent effect auto-routes via Google and stores pathData).
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useRsaPathsContext } from './RsaPathsContext';
import { makeUniqueLabel } from './useRsaPaths';
import { getLrsAdapter, DEFAULT_LRS_STATE } from '../../../../utils/stateLrs/registry';
import { isMeasureInRanges, formatRanges } from '../../../../utils/stateLrs/lrsMath';
import './LrsRoutePanel.css';

const MAX_RESULTS = 50;
const LOCAL_SEARCH_DEBOUNCE_MS = 300;

export default function LrsRoutePanel({ disabled = false }) {
  const ctx = useRsaPathsContext();
  const adapter = getLrsAdapter(DEFAULT_LRS_STATE);

  const [open, setOpen] = useState(false);
  const [statewide, setStatewide] = useState(null);   // RouteEntry[] | null
  const [loadError, setLoadError] = useState(null);
  const [query, setQuery] = useState('');
  const [includeLocal, setIncludeLocal] = useState(false);
  const [localResults, setLocalResults] = useState([]);
  const [selected, setSelected] = useState(null);     // RouteEntry
  const [geometry, setGeometry] = useState(null);     // { sections, mRanges }
  const [geometryError, setGeometryError] = useState(null);
  const [mpStart, setMpStart] = useState('');
  const [endMode, setEndMode] = useState('mp');       // 'mp' | 'len'
  const [mpEnd, setMpEnd] = useState('');
  const [lengthMi, setLengthMi] = useState('');
  const [formError, setFormError] = useState(null);
  const [creating, setCreating] = useState(false);
  const localSearchTimer = useRef(null);
  // Synchronous guard against double-click: `creating` state updates are
  // async, so a second click before the first re-render can slip past the
  // `creating` check in handleCreate and fire two path creations.
  const creatingRef = useRef(false);

  const measureWord = selected?.network === 'milepoint' ? 'milepoint' : 'milepost';

  const loadStatewide = useCallback(() => {
    setLoadError(null);
    adapter.listStatewideRoutes()
      .then(setStatewide)
      .catch((err) => setLoadError(err?.message ?? 'Alabama LRS unavailable'));
  }, [adapter]);

  useEffect(() => {
    if (open && !statewide && !loadError) loadStatewide();
  }, [open, statewide, loadError, loadStatewide]);

  // Debounced live search for local roads.
  useEffect(() => {
    if (!includeLocal || !query.trim()) {
      setLocalResults([]);
      return undefined;
    }
    if (localSearchTimer.current) clearTimeout(localSearchTimer.current);
    localSearchTimer.current = setTimeout(() => {
      adapter.searchLocalRoutes(query)
        .then(setLocalResults)
        .catch(() => setLocalResults([]));
    }, LOCAL_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(localSearchTimer.current);
  }, [includeLocal, query, adapter]);

  const needle = query.trim().toLowerCase();
  const statewideMatches = (statewide ?? [])
    .filter((r) => !needle || r.label.toLowerCase().includes(needle)) // contains, not prefix
    .slice(0, MAX_RESULTS);
  const results = [...statewideMatches, ...localResults].slice(0, MAX_RESULTS);

  const handleSelect = (route) => {
    setSelected(route);
    setGeometry(null);
    setGeometryError(null);
    setFormError(null);
    setMpStart(''); setMpEnd(''); setLengthMi('');
    adapter.getRouteGeometry(route.key)
      .then(setGeometry)
      .catch((err) => setGeometryError(err?.message ?? 'Could not load route geometry'));
  };

  const resetPanel = () => {
    setSelected(null); setGeometry(null); setGeometryError(null);
    setMpStart(''); setMpEnd(''); setLengthMi(''); setFormError(null);
    setQuery('');
  };

  const handleCreate = async () => {
    if (creatingRef.current) return;
    if (!selected || !geometry || creating) return;
    const start = Number(mpStart);
    const end = endMode === 'mp' ? Number(mpEnd) : start + Number(lengthMi);
    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      setFormError('Enter numeric values.');
      return;
    }
    if (end <= start) {
      setFormError(`${measureWord === 'milepost' ? 'MP end' : 'Milepoint end'} must be greater than the start.`);
      return;
    }
    const ranges = geometry.mRanges;
    if (!isMeasureInRanges(ranges, start) || !isMeasureInRanges(ranges, end)) {
      setFormError(`A value is outside the valid ${measureWord} range (${formatRanges(ranges)}).`);
      return;
    }
    creatingRef.current = true;
    setCreating(true);
    setFormError(null);
    try {
      const [ptA, ptB] = await Promise.all([
        adapter.measureToPoint(selected.key, start),
        adapter.measureToPoint(selected.key, end),
      ]);
      if (!ptA || !ptB) {
        setFormError(`Could not locate that ${measureWord} on the route (measure gap).`);
        return;
      }
      const measureAbbrev = measureWord === 'milepost' ? 'MP' : 'MPT';
      const baseLabel = `${selected.label} ${measureAbbrev} ${start.toFixed(1)}-${end.toFixed(1)}`;
      const id = ctx.actions.addPath();
      ctx.actions.updatePath(id, {
        label: makeUniqueLabel(baseLabel, ctx.paths),
        waypoints: [ptA, ptB],
        lrs: {
          state: adapter.stateCode,
          network: selected.network,
          routeKey: selected.key,
          routeIds: (geometry.sections ?? []).map((s) => s.routeId).filter(Boolean),
          routeLabel: selected.label,
          direction: selected.routeDirection,
          mpStart: start,
          mpEnd: end,
        },
      });
      resetPanel();
      setOpen(false);
    } catch (err) {
      setFormError(err?.message ?? 'Failed to create route.');
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  };

  if (!adapter) return null; // no LRS for this state — feature hidden

  const sourceInfo = adapter.getSourceInfo();

  return (
    <div className="lrs-route-panel">
      <button
        type="button"
        className="paths-panel-add-segment-btn lrs-route-panel-toggle"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        title={disabled
          ? 'Finish creating ortho maps for your current route before adding another'
          : `Create a route from ${adapter.stateName} road + milepost data`}
      >
        <i className="fas fa-road" aria-hidden /> Add route by road / milepost
      </button>

      {open && (
        <div className="lrs-route-panel-body">
          <div className="lrs-route-panel-state">{adapter.stateName}</div>

          {loadError && (
            <div className="lrs-route-panel-error" role="alert">
              <span>Alabama LRS unavailable — you can still create routes by clicking the map.</span>
              <button type="button" onClick={loadStatewide}>Retry</button>
            </div>
          )}

          {sourceInfo.statewideSource === 'snapshot' && (
            <div className="lrs-route-panel-notice">
              Road list from LRS snapshot of {String(sourceInfo.snapshotDate).slice(0, 10)}
            </div>
          )}

          {!loadError && (
            <>
              <input
                type="text"
                className="lrs-route-panel-search"
                placeholder="Search roads (e.g. I-65, US-31, 280)…"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setSelected(null); setGeometry(null); }}
              />
              <label className="lrs-route-panel-local-toggle">
                <input
                  type="checkbox"
                  checked={includeLocal}
                  onChange={(e) => setIncludeLocal(e.target.checked)}
                  aria-label="Include local roads"
                />
                Include local roads
              </label>

              {!selected && query.trim() && (
                <ul className="lrs-route-panel-results">
                  {results.map((r) => (
                    <li key={r.key}>
                      <button type="button" onClick={() => handleSelect(r)}>{r.label}</button>
                    </li>
                  ))}
                  {results.length === 0 && statewide && (
                    <li className="lrs-route-panel-noresults">
                      No matches{includeLocal ? ' — refine your search' : ''}
                    </li>
                  )}
                  {results.length >= MAX_RESULTS && (
                    <li className="lrs-route-panel-noresults">Showing first {MAX_RESULTS} — refine your search</li>
                  )}
                </ul>
              )}

              {selected && (
                <div className="lrs-route-panel-form">
                  <div className="lrs-route-panel-selected">
                    <strong>{selected.label}</strong>
                    <button type="button" onClick={resetPanel} title="Pick a different road">×</button>
                  </div>

                  {geometryError && (
                    <div className="lrs-route-panel-error" role="alert">{geometryError}</div>
                  )}
                  {!geometry && !geometryError && (
                    <div className="lrs-route-panel-notice">Loading route geometry…</div>
                  )}
                  {geometry && (
                    <div className="lrs-route-panel-range">
                      Valid {measureWord} range: {formatRanges(geometry.mRanges)}
                    </div>
                  )}

                  <label>
                    {measureWord === 'milepost' ? 'MP start' : 'Milepoint start'}
                    <input
                      type="number" step="0.1" value={mpStart}
                      onChange={(e) => setMpStart(e.target.value)}
                      aria-label={measureWord === 'milepost' ? 'MP start' : 'Milepoint start'}
                    />
                  </label>

                  <div className="lrs-route-panel-endmode" role="radiogroup" aria-label="Second point mode">
                    <label>
                      <input
                        type="radio" name="lrs-end-mode" checked={endMode === 'mp'}
                        onChange={() => setEndMode('mp')}
                      /> {measureWord === 'milepost' ? 'End at milepost' : 'End at milepoint'}
                    </label>
                    <label>
                      <input
                        type="radio" name="lrs-end-mode" checked={endMode === 'len'}
                        onChange={() => setEndMode('len')}
                        aria-label="Length"
                      /> Length
                    </label>
                  </div>

                  {endMode === 'mp' ? (
                    <label>
                      {measureWord === 'milepost' ? 'MP end' : 'Milepoint end'}
                      <input
                        type="number" step="0.1" value={mpEnd}
                        onChange={(e) => setMpEnd(e.target.value)}
                        aria-label={measureWord === 'milepost' ? 'MP end' : 'Milepoint end'}
                      />
                    </label>
                  ) : (
                    <label>
                      Length (miles)
                      <input
                        type="number" step="0.1" min="0" value={lengthMi}
                        onChange={(e) => setLengthMi(e.target.value)}
                        aria-label="Length (miles)"
                      />
                    </label>
                  )}

                  {formError && (
                    <div className="lrs-route-panel-error" role="alert">{formError}</div>
                  )}

                  <button
                    type="button"
                    className="lrs-route-panel-create"
                    onClick={handleCreate}
                    disabled={!geometry || creating}
                  >
                    {creating ? 'Creating…' : 'Create Route'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
